import { Box, Flex, Heading, Icon, Spinner, Text } from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FiAlertCircle } from "react-icons/fi";
import { cyberidCallback, setAuthHeaders, setTokenInLocalStorage } from "@lubbezposrednio-frontend/core";
import { CYBERID_REDIRECT_URI, consumeCyberidCallbackState } from "../../utils/cyberidAuth";

type Phase = "working" | "error";

/**
 * Powrót z CyberID (OIDC). Odczytuje ?code=&state=, waliduje state (anty-CSRF), wymienia code przez nasz
 * backend (/oidc/cyberid/callback) i utrwala sesję DOKŁADNIE jak logowanie hasłem, po czym idzie do /organizator.
 */
export const CyberidCallbackView: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>("working");
  const ran = useRef(false); // StrictMode: nie wymieniaj jednorazowego `code` dwa razy

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    document.title = "CyberID — powrót";

    const code = params.get("code");
    const oauthError = params.get("error");
    const ctx = consumeCyberidCallbackState(params.get("state"));

    if (oauthError || !code || !ctx) {
      setPhase("error");
      return;
    }

    (async () => {
      try {
        const res = await cyberidCallback(code, ctx.verifier, CYBERID_REDIRECT_URI, ctx.nonce);
        const { access_token, expires_in, refresh_expires_in } = res.data;
        setTokenInLocalStorage(access_token, expires_in, refresh_expires_in);
        setAuthHeaders();
        const redirectTo = ctx.redirect && ctx.redirect.startsWith("/organizator") ? ctx.redirect : "/organizator";
        navigate(redirectTo, { replace: true });
      } catch {
        setPhase("error");
      }
    })();
  }, [params, navigate]);

  const card = (children: React.ReactNode) => (
    <Flex minH="100vh" bg="#F7F5FB" align="center" justify="center" px="20px">
      <Box w="100%" maxW="440px" bg="white" borderRadius="22px" boxShadow="0 18px 50px rgba(67,0,152,0.10)" border="1px solid rgba(67,0,152,0.06)" p={{ base: "32px 24px", md: "40px" }} textAlign="center">
        {children}
      </Box>
    </Flex>
  );

  if (phase === "working") {
    return card(
      <>
        <Spinner color="primary" size="lg" thickness="3px" mb="18px" />
        <Text color="gray.500">Logowanie przez CyberID…</Text>
      </>
    );
  }

  return card(
    <>
      <Icon as={FiAlertCircle} boxSize="44px" color="#e51f44" mb="14px" />
      <Heading fontSize="21px" color="primary" mb="8px">
        Nie udało się zalogować
      </Heading>
      <Text fontSize="14px" color="gray.500" mb="22px">
        Logowanie przez CyberID zostało przerwane lub wygasło. Spróbuj ponownie.
      </Text>
      <Text as={Link} to="/logowanie" fontSize="14px" color="accent" fontWeight="700">
        Wróć do logowania
      </Text>
    </>
  );
};
